// components/JournalSection.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ArrowRight, NotebookPen, ChevronDown } from 'lucide-react';
import { Button } from './Button';
import { supabase } from '../lib/supabase';
import { cn } from '../utils/cn';
import { subDays } from 'date-fns';

export default function JournalSection() {
  const navigate = useNavigate();
  const [celebration, setCelebration] = useState('');
  const [yesterdaysTasks, setYesterdaysTasks] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const [userTimeZone, setUserTimeZone] = useState('');

  useEffect(() => {
    async function fetchUserTimeZone() {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user?.id) return;

        const { data: profile } = await supabase
          .from('profiles')
          .select('time_zone')
          .eq('id', session.user.id)
          .single();

        if (profile?.time_zone) {
          setUserTimeZone(profile.time_zone);
        } else {
          // Default to browser's timezone if not set in profile
          setUserTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone);
        }
      } catch (error) {
        console.error('Error fetching user timezone:', error);
        // Fall back to browser's timezone
        setUserTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone);
      }
    }

    fetchUserTimeZone();
  }, []);

  useEffect(() => {
    async function fetchJournalInfo() {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) return;

      // Get yesterday's date in user's timezone
      const yesterday = subDays(new Date(), 1);
      const yesterdayStart = new Date(yesterday);
      yesterdayStart.setHours(0, 0, 0, 0);
      const yesterdayEnd = new Date(yesterday);
      yesterdayEnd.setHours(23, 59, 59, 999);

      const yesterdayTasksRes = await supabase
        .from('tasks')
        .select('*')
        .eq('user_id', session.user.id)
        .eq('completed', true)
        .gte('completed_at', yesterdayStart.toISOString())
        .lte('completed_at', yesterdayEnd.toISOString());

      setYesterdaysTasks(yesterdayTasksRes.data || []);
    }

    fetchJournalInfo();
  }, [userTimeZone]);

  const handleStartJournal = () => {
    if (celebration.trim()) {
      navigate('/journal/new', {
        state: {
          celebration: celebration.trim(),
          startAtQuestion: 1
        }
      });
    }
  };

  return (
    <div className="border rounded-xl overflow-hidden bg-white shadow-lg">
      <button
        onClick={() => setOpen(prev => !prev)}
        className="w-full flex justify-between items-center px-4 py-6 text-left hover:bg-blue-50"
      >
        <div className="flex items-center gap-2 text-[#004060] font-semibold text-xl">
          <NotebookPen className="w-6 h-6 text-[#236caf]" />
          <span>Engine</span>
        </div>

        <ChevronDown
          className={cn(
            'w-5 h-5 text-[#236caf] transition-transform duration-200',
            open && 'rotate-180'
          )}
        />
      </button>

      {open && (
        <div className="p-4 sm:p-6">
          <div className="mb-6">
            <label className="block text-lg text-gray-700 mb-3">
              Start here: What can you celebrate about yesterday?
            </label>
            <textarea
              value={celebration}
              onChange={(e) => setCelebration(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#236caf] focus:border-transparent resize-none h-24"
              placeholder="Think about your wins, progress, and things that went well. (Question 1 of 6)"
              style={{ color: '#374151' }}
            />
            <div className="flex gap-3 mt-3">
              <Button variant="outline" onClick={() => navigate('/journal/history')} className="flex-1">
                History
              </Button>
              <Button
                onClick={handleStartJournal}
                disabled={!celebration.trim()}
                className="flex items-center gap-2 flex-1"
              >
                Continue
                <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
            {yesterdaysTasks.length > 0 && (
              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <p className="font-medium text-gray-700 mb-2">Yesterday's completed tasks:</p>
                <ul className="space-y-2">
                  {yesterdaysTasks.map(task => (
                    <li key={task.id} className="flex items-center gap-2 text-gray-600">
                      <Check className="w-4 h-4 text-green-600" />
                      {task.text}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}